import type { MarketRentAddress } from "api/landlord/market-rent";

export const MARKET_RENT_SUBJECTS_QUERY_KEY = "MARKET_RENT_SUBJECTS";
export const MARKET_RENT_REPORT_QUERY_KEY = "MARKET_RENT_REPORT";
export const MARKET_RENT_COMPS_QUERY_KEY = "MARKET_RENT_COMPS";

export const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY"
] as const;

export const formatAddress = (address?: MarketRentAddress | null): string => {
  if (!address) return "";
  return [
    address.address1,
    address.city,
    [address.state_or_province, address.postal_code].filter(Boolean).join(" ")
  ]
    .filter(Boolean)
    .join(", ");
};

/**
 * Whole-dollar money for table cells and headline figures. Cents are noise at
 * rent scale and the design never shows them.
 */
export const formatMoney = (cents?: number | null): string => {
  if (cents === null || cents === undefined) return "—";
  return `$${Math.round(cents / 100).toLocaleString("en-US")}`;
};

export const formatNumber = (value?: number | null): string => {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("en-US");
};

/** Trims a trailing ".0" so 2 baths reads "2" and 2.5 stays "2.5". */
export const formatDecimal = (value?: number | null): string => {
  if (value === null || value === undefined) return "—";
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

/** "Cedar Hill, TX 75104" — the address without its street line. */
export const formatLocality = (address?: MarketRentAddress | null): string => {
  if (!address) return "";
  return [
    address.city,
    [address.state_or_province, address.postal_code].filter(Boolean).join(" ")
  ]
    .filter(Boolean)
    .join(", ");
};

// API dates are plain "YYYY-MM-DD"; parsed as UTC and printed as UTC so a
// viewer west of Greenwich doesn't see the day before.
const parseDate = (value: string): Date =>
  new Date(`${value.slice(0, 10)}T00:00:00Z`);

/** "Sep 28" */
export const formatShortDate = (value?: string | null): string => {
  if (!value) return "";
  return parseDate(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  });
};

/**
 * "Oct 12, 2026" for a full timestamp (created_at, comps_synced_at), in the
 * viewer's own timezone. Reading a timestamp as a UTC date would show an
 * evening sync in the Americas as the next day.
 */
export const formatTimestampDate = (value?: string | null): string => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

/** "Oct 12, 2026" for a plain "YYYY-MM-DD" date. */
const formatLongDate = (value?: string | null): string => {
  if (!value) return "";
  return parseDate(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  });
};

/** "Jul 14 – Oct 12, 2026" (the year on the start too when they differ). */
export const formatDateWindow = (
  window?: { start_date: string; end_date: string } | null
): string => {
  if (!window) return "";
  const sameYear =
    window.start_date.slice(0, 4) === window.end_date.slice(0, 4);
  const start = sameYear
    ? formatShortDate(window.start_date)
    : formatLongDate(window.start_date);
  return `${start} – ${formatLongDate(window.end_date)}`;
};

export const formatMonthYear = (isoMonth: string): string => {
  const [year, month] = isoMonth.split("-");
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  return date.toLocaleDateString("en-US", {
    month: "short",
    // 4-digit: "Oct 25" scans as the 25th of October on a chart whose points
    // are months, which is exactly the wrong reading.
    year: "numeric",
    timeZone: "UTC"
  });
};
